import { useMemo } from 'react';
import { useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// Constants
import {
  ANIMATION_SPRING_CONFIGS,
  CLAMP_BOTH,
  INTERPOLATE_0_1_CLAMP_BOTH,
} from '@/constants';

// Types
import { AnimationPreset } from '@/types';

interface AnimatedBadgeProps {
  text: string;
  className?: string;
  startFrame?: number;
  springPreset?: AnimationPreset;
}

const SPRING_CONFIGS = {
  ...ANIMATION_SPRING_CONFIGS,
  [AnimationPreset.BOUNCY]: { damping: 12, stiffness: 180, mass: 0.6 },
};

export const AnimatedBadge = ({
  text,
  className = '',
  startFrame = 0,
  springPreset = AnimationPreset.BOUNCY,
}: AnimatedBadgeProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const characters = useMemo(() => text.split(''), [text]);

  // Box expansion animation
  const boxProgress = spring({
    frame: frame - startFrame,
    fps,
    config: {
      damping: 20,
      stiffness: 100,
      mass: 0.8,
    },
  });

  // Width expands from a thin vertical line to full width
  const scaleX = interpolate(
    boxProgress,
    [0, 1],
    [0.01, 1], // From almost 0 (vertical line) to full width
    CLAMP_BOTH,
  );

  const boxOpacity = interpolate(boxProgress, [0, 0.3], [0, 1], CLAMP_BOTH);

  return (
    <div className="mb-3 inline-block">
      <div
        className={className}
        style={{
          transform: `scaleX(${scaleX})`,
          transformOrigin: 'left center',
          opacity: boxOpacity,
          overflow: 'hidden',
        }}
      >
        <span className="inline-block whitespace-nowrap">
          {characters.map((char, index) => {
            // Characters start appearing after box has expanded a bit
            const charStartFrame = startFrame + 15 + index * 1.5; // Stagger by 1.5 frames per character

            const charProgress = spring({
              frame: frame - charStartFrame,
              fps,
              config: SPRING_CONFIGS[springPreset],
            });

            const charOpacity = interpolate(
              charProgress,
              ...INTERPOLATE_0_1_CLAMP_BOTH,
            );

            // Slide from bottom effect
            const translateY = interpolate(
              charProgress,
              [0, 1],
              [30, 0], // Slide up from 30px below
              CLAMP_BOTH,
            );

            // Slight scale for extra smoothness
            const scale = interpolate(
              charProgress,
              [0, 1],
              [0.8, 1],
              CLAMP_BOTH,
            );

            return (
              <span
                key={`${char}-${index}`}
                style={{
                  display: 'inline-block',
                  opacity: charOpacity,
                  transform: `translateY(${translateY}px) scale(${scale})`,
                }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            );
          })}
        </span>
      </div>
    </div>
  );
};
